import { useState, useEffect, useCallback } from 'react';
import { getUserFeedWithPosts, getUserFeedPaginated, FeedEntry } from '../services/feedService';
import { useAuth } from '../contexts/AuthContext';

interface UseFeedOptions {
  limit?: number;
  enablePagination?: boolean;
  /**
   * Optional override for the user whose feed should be loaded.
   * When provided (e.g. admin viewing another user's feed via ?uid=),
   * this takes precedence over the authenticated user's UID.
   */
  userId?: string | null;
}

interface UseFeedReturn {
  feedEntries: FeedEntry[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage user feed data
 * @param options Configuration options for the feed
 * @returns Feed data and management functions
 */
export const useFeed = (options: UseFeedOptions = {}): UseFeedReturn => {
  const { currentUser } = useAuth();
  const { limit = 20, enablePagination = false, userId: userIdOverride } = options;
  const effectiveUserId = userIdOverride || currentUser?.uid || null;

  const [feedEntries, setFeedEntries] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastPostTimestamp, setLastPostTimestamp] = useState<Date | undefined>();

  const loadFeed = useCallback(async (isRefresh = false) => {
    if (!effectiveUserId) return;

    setLoading(true);
    setError(null);

    try {
      let newEntries: FeedEntry[];

      if (enablePagination) {
        // Load with pagination - use undefined timestamp if refreshing to start from beginning
        newEntries = await getUserFeedPaginated(
          effectiveUserId,
          limit,
          isRefresh ? undefined : lastPostTimestamp
        );
      } else {
        // Load all entries (non-paginated mode)
        newEntries = await getUserFeedWithPosts(effectiveUserId);
      }

      if (isRefresh) {
        setFeedEntries(newEntries);
        setLastPostTimestamp(undefined);
      } else {
        setFeedEntries(prev => [...prev, ...newEntries]);
      }

      // Update pagination state
      if (enablePagination) {
        setHasMore(newEntries.length === limit);
        if (newEntries.length > 0) {
          const lastEntry = newEntries[newEntries.length - 1];
          const timestamp = lastEntry.post.postTimestamp?.toDate?.() || new Date(0);
          setLastPostTimestamp(timestamp);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading feed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, limit, enablePagination, lastPostTimestamp]);

  const loadMore = useCallback(async () => {
    if (!enablePagination || !hasMore || loading) return;
    await loadFeed(false);
  }, [enablePagination, hasMore, loading, loadFeed]);

  const refresh = useCallback(async () => {
    await loadFeed(true);
  }, [loadFeed]);

  // Load initial feed (and reload when the effective user changes)
  useEffect(() => {
    if (effectiveUserId) {
      // Reset state when target user changes
      setFeedEntries([]);
      setLastPostTimestamp(undefined);
      setHasMore(true);
      loadFeed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUserId]);

  return {
    feedEntries,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  };
};

/**
 * Hook to get feed data without pagination (simpler version)
 * @returns Feed data and management functions
 */
export const useSimpleFeed = () => {
  const { currentUser } = useAuth();
  const [feedEntries, setFeedEntries] = useState<FeedEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFeed = useCallback(async (isRefresh = false) => {
    if (!currentUser?.uid) return;

    // Only show loading spinner if there's no existing data
    if (feedEntries.length === 0) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      const entries = await getUserFeedWithPosts(currentUser.uid);
      setFeedEntries(entries);
    } catch (err) {
      console.error('Error loading feed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser?.uid, feedEntries.length]);

  const refresh = useCallback(async () => {
    await loadFeed(true);
  }, [loadFeed]);

  // Load initial feed
  useEffect(() => {
    if (currentUser?.uid) {
      loadFeed(false);
    }
  }, [currentUser?.uid]);

  return {
    feedEntries,
    loading,
    refreshing,
    error,
    refresh
  };
};
